import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { ReCaptchaProvider, ReCaptchaV2, ReCaptchaV3 } from "react-recaptcha-x";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";

import { toAbsoluteUrl } from "../../../_metronic/_helpers";
import { config } from "../../../config/env";
import traceId from "../../../redux/traceId";
import { ModalFailedPayment } from "../../components/ModalFailedPayment";
import { ModalProcessingPayment } from "../../components/ModalProcessingPayment";
import { ModalPaymentSuccess } from "../../components/ModalPaymentSuccess";
import { ModalCheckoutPixPayment } from "../../components/ModalCheckoutPixPayment";
import useFeatureFlag from "../../hooks/useFeatureFlag";
import { useRecaptchaV2, useRecaptchaV3 } from "../../hooks/useRecaptcha";
import { FEATURE_FLAGS } from "../../constants/FeatureFlag";
import { FIRST_ACCESS_ROUTES } from "../FirstAccess/routes";
import { tracking } from "../../services/tracking";
import { createHubspotContact } from "../../modules/Utils/hubpspot/api";
import getCookie from "../../helpers/Cookies";
import {
  trackCheckoutStep2Advanced,
  trackCheckoutStep2AdvancedFailed,
  trackCheckoutSignUpAttempt,
  trackSignUpGeneralInfoStepAdvanced,
  trackSignUpAccountCreationFailed,
} from "app/services/tracking/payments/checkoutStepsEvents";
import { trackSignupCreateAccountButtonClicked } from "app/services/tracking/payments/completeProfileEvents";
import { identifyAnonymousTraits } from "../../services/tracking/segmentIdentity";
import { reconcileCatalogPlan, resolvePlanFromParam } from "../Cadastro/plans";
import { isCaaPrSpecialCoupon } from "../Cadastro/caaPartnerOabCoupons";
import { OabPartnershipModal } from "../Cadastro/components/OabPartnershipModal";

import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  ResultState,
  ResultIcon,
  ResultTitle,
  ResultMessage,
} from "app/components/DialogFrame/styles";
import { PrimaryButton } from "app/components/DialogFooterButtons/styles";
import IdentificationSection from "./components/IdentificationSection";
import CardSection from "./components/CardSection";
import AddressSection from "./components/AddressSection";
import PlanSummary from "./components/PlanSummary";
import PlanDetailsModal from "./components/PlanDetailsModal";
import EmailExistsModal from "./components/EmailExistsModal";
import CapicandiaOnboarding from "./components/CapicandiaOnboarding";
import InfoprodutoresSuccessModal from "./components/InfoprodutoresSuccessModal";
import useCheckoutFlow from "./useCheckoutFlow";
import { persistClient, requestReactivationPaymentLink } from "./api";
import {
  buildValidationSchema,
  initialFormValues,
  isReferencedCoupon,
  isCapicandiaCheckoutCoupon,
  getCapicandiaCheckoutPeriodicity,
  getCapicandiaReactivateLabel,
  getInfoprodutoresCoupon,
} from "./validation";
import * as S from "./styles";
import { useCheckoutPlans } from "./useCheckoutPlans";
import { useTrackCheckoutStarted } from "./useTrackCheckoutStarted";
import {
  useCheckoutProviderMode,
  resolveEffectiveJussubEnabled,
} from "./useCheckoutProviderMode";

const IDENTIFICATION_FIELDS = [
  "name",
  "document_number",
  "email",
  "phone",
  "password",
  "confirmation_password",
];

const NON_IDENTIFICATION_FOCUS_ORDER = [
  "cardholder_name",
  "card_number",
  "card_expiration",
  "card_cvv",
  "postal_code",
  "state",
  "city",
  "address",
  "address_number",
  "address_complement",
  "plan",
  "reference_name",
  "reference_city",
];

const focusFirstInvalidField = (errors, fieldFocusOrder, attempt = 0) => {
  const ordered = fieldFocusOrder.filter(f => errors[f]);
  const remaining = Object.keys(errors).filter(
    f => !fieldFocusOrder.includes(f)
  );
  const target = ordered[0] || remaining[0];
  if (!target) return;
  const el = document.querySelector(`[name="${target}"]`);
  if (!el) {
    // Field may live inside a collapsed section that is expanding on re-render
    if (attempt < 3) {
      setTimeout(
        () => focusFirstInvalidField(errors, fieldFocusOrder, attempt + 1),
        150
      );
    }
    return;
  }
  const scrollTarget = el.closest("section") || el;
  scrollTarget.scrollIntoView({ behavior: "smooth", block: "center" });
  if (typeof el.focus === "function") {
    setTimeout(() => {
      try {
        el.focus({ preventScroll: true });
      } catch (_) {
        el.focus();
      }
    }, 300);
  }
};

export default function CadastroV2(props) {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState("idle");
  const [failedReason, setFailedReason] = useState({ message: "", details: "" });
  const [pixPaymentData, setPixPaymentData] = useState(null);
  const [pixGenerationFailed, setPixGenerationFailed] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const [formErrors, setFormErrors] = useState([]);
  const [uniqueEmail, setUniqueEmail] = useState(null);
  const [showEmailExistsModal, setShowEmailExistsModal] = useState(false);
  const [existsModalReason, setExistsModalReason] = useState("email");
  const [reactivating, setReactivating] = useState(false);
  const [uniqueDocument, setUniqueDocument] = useState(false);
  const [shouldValidateDocument, setShouldValidateDocument] = useState(true);
  const [isCepValid, setIsCepValid] = useState(true);
  // Controla o ciclo dos catálogos que possuem variantes mensal/anual e também
  // o fluxo Capicandia, cujo cupom define uma periodicidade fixa.
  const [periodicity, setPeriodicity] = useState("monthly");
  const [enableRecaptchaV2, setEnableRecaptchaV2] = useState(false);
  const [showPlanDetails, setShowPlanDetails] = useState(false);
  const [onboardingDone, setOnboardingDone] = useState(false);
  const [isOabPartnershipModalOpen, setIsOabPartnershipModalOpen] = useState(
    false
  );

  // Modo infoprodutor (spec checkout-jusfy-cupom-trial): decidido no mount, de
  // forma síncrona, pelo cupom da URL e fixado em ref — mesmo padrão do gate
  // Capicandia (capicandiaGateEligibleRef). O cupom só chega ao redux depois
  // do LOAD_COUPOM; a copy fixa do painel não pode piscar nem depender desse
  // round-trip, e trocar o cupom no meio do checkout não pode mudar o modo.
  const infoprodutoresCouponRef = useRef(
    getInfoprodutoresCoupon(props.match?.params?.redirect)
  );
  const infoprodutoresCoupon = infoprodutoresCouponRef.current;
  const isInfoprodutoresMode = Boolean(infoprodutoresCoupon);

  const subscription_error = useSelector(
    state => state.subscription.subscription_error
  );
  const coupom = useSelector(state => state.app.coupom);
  // Enquanto o cupom valida (cascata Jussub→Jusbill), coupom.is_jussub ainda não existe e
  // resolveEffectiveJussubEnabled cai pra flag crua — sem bloquear o submit aqui, dava pra
  // finalizar o checkout com o provider errado nessa janela (mesmo motivo do bug que motivou
  // a cascata: decidir o provider antes do cupom terminar de resolver).
  const isLoadingCoupom = useSelector(state => state.app.is_loading_coupom);
  // Última resolução definitiva de coupom.is_jussub (true/false/null). O próprio
  // LOAD_COUPOM zera coupom pra {} enquanto valida — sem preservar essa resolução, o
  // provider efetivo oscilava de volta pro valor cru da flag só durante o loading,
  // fazendo o useCheckoutPlans trocar de catálogo (e possivelmente resetar o plano
  // selecionado) por causa de um estado puramente transitório, mesmo com o submit já
  // bloqueado nessa janela. Só atualiza numa resolução real, nunca no reset de loading.
  const lastCoupomIsJussubRef = useRef(null);
  useEffect(() => {
    if (typeof coupom?.is_jussub === "boolean" || coupom?.is_jussub === null) {
      lastCoupomIsJussubRef.current = coupom.is_jussub;
    }
  }, [coupom]);
  const stableCoupom = isLoadingCoupom
    ? { is_jussub: lastCoupomIsJussubRef.current }
    : coupom;
  const defaultTrialDays = useSelector(state => state.app.default_trial_days);

  // const disabledCreditCard = useSelector(state =>
  //   Object.keys(state.app.coupom).length > 0 && state.app.coupom?.is_valid
  //     ? !state?.app?.coupom?.upfront_credit_card_required
  //     : false
  // );
  const disabledCreditCard = false;

  const { isFeatureFlagEnable: enableCustomerCreate, flagsReady: ccReady } =
    useFeatureFlag(FEATURE_FLAGS.KILL_SWITCH.ENABLE_CHECKOUT_CUSTOMER_CREATE);

  // A senha do aluno viaja até o infoprodutores-service (spec D11): só o modo
  // infoprodutor pede senha; o checkout Jusfy é sempre sem senha.
  const isPasswordlessCheckout = !isInfoprodutoresMode;
  const checkoutProviderMode = useCheckoutProviderMode();
  const effectiveIsJussubEnabled = resolveEffectiveJussubEnabled({
    isJussubEnabled: checkoutProviderMode.isJussubEnabled,
    coupom: stableCoupom,
  });

  const identificationFields = useMemo(
    () =>
      IDENTIFICATION_FIELDS.filter(
        field =>
          !isPasswordlessCheckout ||
          !["password", "confirmation_password"].includes(field)
      ),
    [isPasswordlessCheckout]
  );

  const fieldFocusOrder = useMemo(
    () => [...identificationFields, ...NON_IDENTIFICATION_FOCUS_ORDER],
    [identificationFields]
  );

  const search = new URLSearchParams(location.search);
  const hasCompraGarantida = search.get("compra_garantida") === "true";

  const showCupomReferenceInputs =
    coupom?.is_valid && isReferencedCoupon(coupom?.code);

  const validationSchema = useMemo(
    () =>
      buildValidationSchema({
        disabledCreditCard,
        isPasswordlessCheckout,
        setUniqueEmail,
        setUniqueDocument,
        setShouldValidateDocument,
        shouldValidateDocument,
        isCepValid,
        showCupomReferenceInputs,
        isInfoprodutoresMode,
      }),
    [
      disabledCreditCard,
      isPasswordlessCheckout,
      shouldValidateDocument,
      isCepValid,
      showCupomReferenceInputs,
      isInfoprodutoresMode,
    ]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialFormValues,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: values => flow.onSubmit(values),
  });

  const {
    plans,
    isLoaded,
    isError: isPricingCatalogBlockingError,
    refetch: refetchPricingCatalog,
    offers: pricingCatalogOffers,
  } = useCheckoutPlans({
    isJussubEnabled: effectiveIsJussubEnabled,
    isJussubResolved: checkoutProviderMode.isResolved,
    coupomCode: formik.values.coupom,
  });

  const isCapicandiaCheckout =
    isCapicandiaCheckoutCoupon(coupom?.code) ||
    isCapicandiaCheckoutCoupon(formik.values.coupom);

  // trava o grid num card só quando ?plan= resolve explicitamente pra um plano. O cupom "dono de
  // tier" do v2 passa por /coupons/validate (isCouponValid em PlanSummary), que já colapsa o grid
  // sozinho — não precisa entrar nessa conta. Nunca trava no fluxo Capicandia: a seleção de
  // plano lá é toda ditada pelo cupom Capicandia (cards/periodicidade próprios), fora do nosso
  // controle — sobrescrever com lockedPlan quebraria esse fluxo.
  const lockedPlan =
    plans.length && !isCapicandiaCheckout
      ? resolvePlanFromParam(plans, new URLSearchParams(location.search).get("plan"))
      : undefined;

  const flow = useCheckoutFlow({
    formik,
    locationSearch: location.search,
    setSubmitting,
    setResult,
    setFailedReason,
    setPixPaymentData,
    setPixGenerationFailed,
    setDisableButton,
    setEnableRecaptchaV2,
    formErrors,
    isJussubEnabled: effectiveIsJussubEnabled,
    isJussubResolved: checkoutProviderMode.isResolved,
    pricingCatalogOffers,
    isInfoprodutoresMode,
    infoprodutoresCoupon,
  });
  const planSetByParamRef = useRef(false);

  const [handleRecaptchaV2Callback, captchaV2Params] = useRecaptchaV2();
  const [handleRecaptchaV3Callback, captchaV3Params] = useRecaptchaV3(
    flow.refreshRecaptchaV3Ref
  );

  useEffect(() => {
    formik.setFieldValue("recaptchaVersion", "3");
    formik.setFieldValue("recaptchaToken", captchaV3Params.v3Token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captchaV3Params.v3Token]);

  useEffect(() => {
    formik.setFieldValue("recaptchaVersion", "2");
    formik.setFieldValue("recaptchaToken", captchaV2Params.v2Token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captchaV2Params.v2Token]);

  useEffect(() => {
    if (enableRecaptchaV2) {
      setDisableButton(!captchaV2Params.v2Token);
    } else {
      setDisableButton(captchaV3Params.v3Retrieving || !captchaV3Params.v3Token);
    }
  }, [
    enableRecaptchaV2,
    captchaV2Params.v2Token,
    captchaV3Params.v3Retrieving,
    captchaV3Params.v3Token,
  ]);

  useEffect(() => {
    if (subscription_error) {
      setResult("error");
      setSubmitting(false);
    }
  }, [subscription_error]);

  const dismissedEmailRef = useRef(null);
  const dismissedDocumentRef = useRef(null);
  const emailInputRef = useRef(null);

  // Gate de onboarding Capicandia: decidido no mount, de forma síncrona, pelo
  // cupom da URL — evita o flash do formulário enquanto o LOAD_COUPOM resolve via
  // redux. Fixado em ref de propósito: se o usuário aplicar um cupom Capicandia
  // no campo do resumo já no meio do checkout, o gate não deve reaparecer e
  // substituir o formulário que ele estava preenchendo.
  const capicandiaGateEligibleRef = useRef(
    isCapicandiaCheckoutCoupon(props.match?.params?.redirect?.toUpperCase())
  );
  const showOnboardingGate = capicandiaGateEligibleRef.current && !onboardingDone;

  useEffect(() => {
    const currentEmail = formik.values.email;
    if (
      uniqueEmail === currentEmail &&
      !formik.isValidating &&
      currentEmail !== dismissedEmailRef.current
    ) {
      setExistsModalReason("email");
      setShowEmailExistsModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueEmail, formik.values.email, formik.isValidating]);

  useEffect(() => {
    if (!isCapicandiaCheckout) return;
    const currentDocument = formik.values.document_number;
    if (
      uniqueDocument &&
      !formik.isValidating &&
      currentDocument !== dismissedDocumentRef.current &&
      !showEmailExistsModal
    ) {
      setExistsModalReason("document");
      setShowEmailExistsModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    uniqueDocument,
    formik.values.document_number,
    formik.isValidating,
    isCapicandiaCheckout,
  ]);

  const handleReactivateAccess = async () => {
    if (reactivating) return;
    setReactivating(true);
    try {
      const isDocumentReason = existsModalReason === "document";
      const response = await requestReactivationPaymentLink({
        email: isDocumentReason ? undefined : formik.values.email,
        document: isDocumentReason
          ? formik.values.document_number?.replace(/\D/g, "")
          : undefined,
        plan: coupom?.plan_id ?? formik.values.plan?.id,
      });
      const action = response?.data?.action;
      if (action === "ADDON_CHECKOUT") {
        const capicandiaPeriodicity = getCapicandiaCheckoutPeriodicity(
          coupom?.code || formik.values.coupom
        );
        const cycle =
          capicandiaPeriodicity === "annually" ? "ANNUAL" : "MONTHLY";
        history.push(`/addons/capicandia/checkout?cycle=${cycle}`);
        return;
      }
      const paymentLink = response?.data?.payment_link;
      if (!paymentLink) {
        throw new Error("Reactivation payment link missing in response");
      }
      window.location.href = paymentLink;
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: "capicandia_reactivation_link" },
      });
      toast.error(
        "Não foi possível gerar o link de reativação. Tente novamente."
      );
      setReactivating(false);
    }
  };

  useEffect(() => {
    dispatch({ type: "LOAD_STATES" });
    dispatch({ type: "LOAD_PROFESSIONS" });

    const redirectParam = props.match?.params?.redirect;
    if (redirectParam) {
      const planMatch = plans.find(
        p => p.id.toLowerCase() === redirectParam.toLowerCase()
      );
      if (planMatch) {
        planSetByParamRef.current = true;
        formik.setFieldValue("plan", planMatch);
      } else {
        const couponInRedirect = redirectParam.toUpperCase();
        formik.setFieldValue("coupom", couponInRedirect);
        requestedPlanIdRef.current = formik.values.plan?.id;
        dispatch({
          type: "LOAD_COUPOM",
          payload: {
            coupom: couponInRedirect,
            tryJussub: true,
            planId: formik.values.plan?.id,
          },
        });
        if (isCapicandiaCheckoutCoupon(couponInRedirect)) {
          const capicandiaPeriodicity =
            getCapicandiaCheckoutPeriodicity(couponInRedirect);
          if (capicandiaPeriodicity) setPeriodicity(capicandiaPeriodicity);
        }
        if (isCaaPrSpecialCoupon(couponInRedirect)) {
          setIsOabPartnershipModalOpen(true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useTrackCheckoutStarted({
    formValues: formik.values,
    isCatalogSettled: isLoaded || isPricingCatalogBlockingError,
  });

  // O cupom da URL já é validado no mount pela cascata Jussub→Jusbill (LOAD_COUPOM,
  // tryJussub). O /validity do Jussub precifica o cupom PARA o plano selecionado e o
  // grid segue visível no Jussub (isJussubCouponActive), então só um cupom que já
  // resolveu como Jussub precisa revalidar a CADA troca de plano pra reprecificar —
  // revalidar um cupom Jusbill a cada troca seria um round-trip (Jussub→Jusbill) sem
  // efeito nenhum no preço.
  const selectedPlanId = formik.values.plan?.id;
  const didMountPricingEffectRef = useRef(false);
  // planId que a validação mais recente (mount ou a última troca) usou. Comparado
  // contra selectedPlanId sempre que `coupom` muda — não só quando o plano muda —
  // porque a resposta pode chegar DEPOIS que o usuário já trocou de plano de novo (ex.:
  // a validação do mount só confirma is_jussub:true depois que um plano já foi
  // escolhido); sem essa checagem no `coupom`, essa resposta tardia nunca revalida pro
  // plano atual e o preço mostrado fica preso ao plano errado (ou a nenhum).
  const requestedPlanIdRef = useRef(selectedPlanId);
  useEffect(() => {
    if (!didMountPricingEffectRef.current) {
      // 1ª validação já saiu no efeito de mount, com o planId inicial.
      didMountPricingEffectRef.current = true;
      return;
    }
    const activeCoupon = formik.values.coupom;
    // lastCoupomIsJussubRef (não coupom.is_jussub direto) porque o LOAD_COUPOM abaixo
    // zera coupom pra {} assim que dispara — ler o valor ao vivo aqui perderia a
    // informação numa 2ª troca de plano com a cascata da 1ª ainda em voo.
    if (
      !activeCoupon ||
      lastCoupomIsJussubRef.current !== true ||
      requestedPlanIdRef.current === selectedPlanId
    ) {
      return;
    }
    requestedPlanIdRef.current = selectedPlanId;
    dispatch({
      type: "LOAD_COUPOM",
      payload: {
        coupom: activeCoupon,
        tryJussub: true,
        planId: selectedPlanId,
      },
    });
    // Depende de selectedPlanId E coupom: troca de plano cobre o caso comum (cupom já
    // sabido Jussub, usuário troca de plano); coupom cobre o caso tardio acima.
    // requestedPlanIdRef, atualizado só quando este efeito de fato dispara, evita
    // disparo duplicado quando o próprio LOAD_COUPOM muda `coupom` (loading, depois
    // sucesso) pro MESMO plano — takeLatest (appRedux.loadCoupom) cancela a cascata
    // anterior ainda em voo a cada novo disparo daqui, então uma 2ª troca de plano
    // antes da resposta da 1ª chegar já pede certo pro plano atual.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlanId, coupom]);

  useEffect(() => {
    const value = hasCompraGarantida && !(coupom && coupom.is_valid);
    formik.setFieldValue("has_compra_garantida", value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCompraGarantida, coupom]);

  // Lead persistence: as soon as identification fields are valid, persist client
  const persistedSignatureRef = useRef(null);
  const identifiedSignatureRef = useRef(null);
  const hubspotSignatureRef = useRef(null);
  const analyticsFiredRef = useRef(false);
  useEffect(() => {
    if (!ccReady) return;
    const v = formik.values;
    const allFilled = identificationFields.every(f => !!v[f]);
    const noIdentificationErrors = identificationFields.every(
      f => !formik.errors[f]
    );
    if (
      !allFilled ||
      !noIdentificationErrors ||
      uniqueEmail ||
      uniqueDocument ||
      formik.isValidating
    ) {
      return;
    }

    const signature = `${v.name}|${v.email}`;

    if (identifiedSignatureRef.current !== signature) {
      identifiedSignatureRef.current = signature;
      identifyAnonymousTraits({ name: v.name, email: v.email });
    }

    if (
      hubspotSignatureRef.current !== signature &&
      process.env.NODE_ENV === "production"
    ) {
      hubspotSignatureRef.current = signature;
      const hutk = getCookie("hubspotutk");
      createHubspotContact({ formik }, hutk, "App");
    }

    if (persistedSignatureRef.current === signature) return;
    persistedSignatureRef.current = signature;

    const persist = async () => {
      try {
        if (enableCustomerCreate) {
          const response = await persistClient({
            name: v.name,
            email: v.email,
          });
          formik.setFieldValue("createdCustomerId", response?.data?.id);
        }
        if (!analyticsFiredRef.current) {
          analyticsFiredRef.current = true;
          trackSignUpGeneralInfoStepAdvanced();
          const step2AdvancedProps =
            tracking.checkout.prepareCheckoutStep2AdvancedForAnalytics(
              formik.values,
              {}
            );
          trackCheckoutStep2Advanced(step2AdvancedProps);
        }
      } catch (error) {
        persistedSignatureRef.current = null;
        const failedProps =
          tracking.checkout.prepareCheckoutStep2AdvancedFailedForAnalytics(
            formik.values,
            null,
            {
              type: "error_persisting_customer",
              message: error?.message || "Erro ao persistir cliente",
              code: error?.response?.status || "unknown",
            }
          );
        trackCheckoutStep2AdvancedFailed(failedProps);
        Sentry.captureException(error);
      }
    };
    persist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values,
    formik.errors,
    formik.isValidating,
    uniqueEmail,
    uniqueDocument,
    ccReady,
    enableCustomerCreate,
    identificationFields,
  ]);

  useEffect(() => {
    if (!isLoaded) return;

    const current = formik.values.plan;
    const planParam = new URLSearchParams(location.search).get("plan");
    // `plans` troca de identidade a cada cupom/ciclo de loading (novo
    // array do catálogo da API); reconcilia por id em vez de tratar a troca de referência como
    // "plano sumiu do catálogo" (o que resetava a seleção pro default a cada cupom aplicado).
    const reconciled = reconcileCatalogPlan({
      plans,
      selectedPlan: current,
      planParam,
    });

    if (!reconciled) return;

    if (planParam && reconciled === resolvePlanFromParam(plans, planParam)) {
      planSetByParamRef.current = true;
    } else if (planSetByParamRef.current) {
      return;
    }

    if (reconciled !== current) {
      if (["monthly", "annually"].includes(reconciled.type)) {
        setPeriodicity(reconciled.type);
      }
      formik.setFieldValue("plan", reconciled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, plans]);

  const handleSubmitForm = async () => {
    trackSignupCreateAccountButtonClicked({
        plan_id: formik.values?.plan?.id,
        plan_type: formik.values?.plan?.type,
        has_coupom: !!formik.values?.coupom,
        coupom_code: formik.values?.coupom || null,
        has_compra_garantida: hasCompraGarantida,
        state_uf: formik.values?.state?.state || null,
        card_filled: !!(
          formik.values?.card_number &&
          formik.values?.card_cvv &&
          formik.values?.card_expiration
        ),
        recaptcha_version: formik.values?.recaptchaVersion,
      });

    const checkoutSignUpAttemptProps =
      tracking.checkout.prepareCheckoutSignUpAttemptForAnalytics(
        formik.values,
        coupom,
        defaultTrialDays
      );
    trackCheckoutSignUpAttempt(checkoutSignUpAttemptProps);

    setEnableRecaptchaV2(false);

    const validation = await formik.validateForm();
    const touchedMap = Object.keys(validation).reduce(
      (acc, field) => ({ ...acc, [field]: true }),
      {}
    );
    formik.setTouched(touchedMap);
    if (Object.keys(validation).length === 0) {
      if (coupom?.is_partner_coupon) {
        try {
          await axios.post(
            `${config.jusfyBackend.apiUrl}/checkout-validation`,
            {
              document: formik.values.document_number?.replace(/\D/g, ""),
              coupon: formik.values.coupom,
            },
            { headers: { "x-trace-id": traceId }, timeout: 10000 }
          );
        } catch (validationError) {
          setDisableButton(false);
          if (validationError?.response) {
            toast.error(validationError.response.data.message);
          } else {
            toast.error(
              "Não foi possível validar o cupom da parceria. Tente novamente."
            );
          }
          return;
        }
      }
      setSubmitting(true);
      setResult("idle");
      formik.handleSubmit();
      return;
    }
    const firstErrorKey = Object.keys(formik.errors)[0];
    const firstErrorMessage = formik.errors[firstErrorKey];
    const currentErrors = Object.entries(validation).map(([field, error]) => ({
      field,
      error,
      timestamp: new Date().toISOString(),
    }));
    setFormErrors(prev => [...prev, ...currentErrors]);
    trackSignUpAccountCreationFailed({
        error: "FORM_VALUES_INVALID",
        message: firstErrorMessage,
        validation,
      });
    focusFirstInvalidField(validation, fieldFocusOrder);
  };

  const reset = () => {
    setSubmitting(false);
    setResult("idle");
    setFailedReason({ message: "", details: "" });
    setPixGenerationFailed(false);
    setDisableButton(false);
    setEnableRecaptchaV2(false);
    dispatch({ type: "CLEAR_SUBSCRIPTION_ERROR" });
  };

  const handleOabPartnershipCouponApplied = coupon => {
    formik.setFieldValue("coupom", coupon);
    requestedPlanIdRef.current = formik.values.plan?.id;
    dispatch({
      type: "LOAD_COUPOM",
      payload: {
        coupom: coupon,
        tryJussub: true,
        planId: formik.values.plan?.id,
      },
    });
  };

  const onTryAgain = () => flow.onTryAgain(reset);
  const onAccessPlatform = () => {
    const origin = pixGenerationFailed
      ? "pix-generation-failed-modal-checkout"
      : result === "pix-success"
        ? "pix-success-modal-checkout"
        : "failed-modal-checkout";
    flow.onAccessPlatform(origin, () => {
      reset();
      history.push(FIRST_ACCESS_ROUTES.BASE, {
        email: formik.values.email,
        fromCheckout: true,
      });
    });
  };

  const handleAccessInfoprodutor = () => {
    // onAccessPlatform trackeia o clique e, como o cupom não é Capicandia,
    // chama o callback — aqui o destino é o login do app do infoprodutor,
    // com o e-mail pré-preenchido (spec §4.5 "Login: ler ?email=").
    flow.onAccessPlatform("infoprodutores-success-modal-checkout", () => {
      window.location.href = `${
        infoprodutoresCoupon.app_url
      }/login?email=${encodeURIComponent(formik.values.email)}`;
    });
  };

  const planAmountNumber = Number(
    String(coupom?.amount || formik.values.plan?.amount || "0").replace(
      /[^0-9]/g,
      ""
    )
  );

  const identificationDone = identificationFields.every(
    f => !!formik.values[f] && !formik.errors[f]
  );
  const CARD_FIELDS = ["cardholder_name", "card_number", "card_expiration", "card_cvv"];
  const ADDRESS_FIELDS = ["postal_code", "state", "city", "district", "address", "address_number"];
  const cardDone =
    disabledCreditCard ||
    (CARD_FIELDS.every(f => !!formik.values[f]) &&
      CARD_FIELDS.every(f => !formik.errors[f]));
  const addressDone =
    isCepValid &&
    ADDRESS_FIELDS.every(f => !!formik.values[f]) &&
    ADDRESS_FIELDS.every(f => !formik.errors[f]);

  return (
    <>
      <S.PageGlobalStyle />
      <S.Page>
        <S.TopBar>
          <img
            src={toAbsoluteUrl("/media/svg/logos/logo-jusfy.svg")}
            alt="Jusfy"
          />

          <S.Footnote>
            <LockOutlinedIcon sx={{ fontSize: 16, color: '#838486 !important' }} /> Compra segura | Jusfy {new Date().getFullYear()}
          </S.Footnote>
        </S.TopBar>

        {showOnboardingGate ? (
          <CapicandiaOnboarding
            coupom={coupom}
            formik={formik}
            history={history}
            onComplete={() => setOnboardingDone(true)}
          />
        ) : (
        <S.Container>
          <div>
            <S.Heading>
              <h1>Detalhes do pagamento</h1>
              <p>Insira as informações solicitadas para concluir o pagamento.</p>
            </S.Heading>

            <IdentificationSection
              formik={formik}
              isUniqueEmail={uniqueEmail}
              isUniqueDocument={uniqueDocument}
              completed={identificationDone}
              emailInputRef={emailInputRef}
              isPasswordlessCheckout={isPasswordlessCheckout}
              active
            />

            <CardSection
              formik={formik}
              completed={cardDone}
              active={identificationDone}
              hidden={disabledCreditCard}
            />

            <AddressSection
              formik={formik}
              completed={addressDone}
              active={disabledCreditCard ? identificationDone : cardDone}
              setIsCepValid={setIsCepValid}
              isCepValid={isCepValid}
              step={disabledCreditCard ? 2 : 3}
            />

            <ReCaptchaProvider
              siteKeyV2={process.env.REACT_APP_RECAPTCHA_V2_SITEKEY}
              siteKeyV3={process.env.REACT_APP_RECAPTCHA_V3_SITEKEY}
            >
              {!enableRecaptchaV2 && (
                <ReCaptchaV3
                  callback={handleRecaptchaV3Callback}
                  action="login"
                />
              )}
              {enableRecaptchaV2 && (
                <ReCaptchaV2
                  callback={handleRecaptchaV2Callback}
                  className="my-4"
                />
              )}
            </ReCaptchaProvider>
          </div>

          {isLoaded || isPricingCatalogBlockingError ? (
            <>
              {isPricingCatalogBlockingError && (
                <S.Summary role="alert">
                  <ResultState>
                    <ResultIcon $variant="error">
                      <ErrorOutlineIcon />
                    </ResultIcon>
                    <ResultTitle>Não foi possível carregar os planos</ResultTitle>
                    <ResultMessage>
                      Tente novamente em instantes ou entre em contato com o suporte.
                    </ResultMessage>
                    <PrimaryButton type="button" onClick={refetchPricingCatalog}>
                      Tentar novamente
                    </PrimaryButton>
                  </ResultState>
                </S.Summary>
              )}
              <PlanSummary
                formik={formik}
                plans={plans}
                periodicity={periodicity}
                setPeriodicity={setPeriodicity}
                onSubmit={handleSubmitForm}
                disableButton={
                  disableButton || isPricingCatalogBlockingError || isLoadingCoupom
                }
                submitting={submitting}
                hasCompraGarantida={hasCompraGarantida}
                onShowDetails={() => setShowPlanDetails(true)}
                isInfoprodutoresMode={isInfoprodutoresMode}
                infoprodutoresCoupon={infoprodutoresCoupon}
                isJussubEnabled={effectiveIsJussubEnabled}
                requestedPlanIdRef={requestedPlanIdRef}
                lockedPlan={lockedPlan}
              />
            </>
          ) : (
            <S.Summary role="status" aria-live="polite">
              Carregando planos...
            </S.Summary>
          )}
        </S.Container>
        )}
      </S.Page>

      <EmailExistsModal
        open={showEmailExistsModal}
        email={formik.values.email}
        documentNumber={formik.values.document_number}
        reason={existsModalReason}
        onChangeEmail={() => {
          setShowEmailExistsModal(false);
          if (existsModalReason === "document") {
            dismissedDocumentRef.current = formik.values.document_number;
            setTimeout(() => {
              document
                .querySelector('[name="document_number"]')
                ?.focus();
            }, 50);
            return;
          }
          dismissedEmailRef.current = formik.values.email;
          setUniqueEmail(null);
          setTimeout(() => emailInputRef.current?.focus(), 50);
        }}
        onLogin={() => {
          if (existsModalReason === "document") {
            dismissedDocumentRef.current = formik.values.document_number;
          } else {
            dismissedEmailRef.current = formik.values.email;
          }
          setShowEmailExistsModal(false);
          history.push("/auth/login");
        }}
        onReactivate={isCapicandiaCheckout ? handleReactivateAccess : undefined}
        reactivating={reactivating}
        reactivateLabel={getCapicandiaReactivateLabel(
          coupom?.code || formik.values.coupom
        )}
      />

      <PlanDetailsModal
        open={showPlanDetails}
        onClose={() => setShowPlanDetails(false)}
        formik={formik}
        plans={plans}
      />

      <ModalProcessingPayment
        isOpen={submitting && result !== "error"}
        {...(isInfoprodutoresMode
          ? {
              title: "Estamos criando sua conta",
              subtitle: `Em instantes, você terá acesso à ${infoprodutoresCoupon.product_name}.`,
            }
          : {})}
      />
      <ModalPaymentSuccess
        isOpen={result === "success"}
        email={formik.values.email}
        onAccessPlatform={onAccessPlatform}
      />
      <ModalFailedPayment
        isOpen={result === "error"}
        title={failedReason.message}
        subTitle={failedReason.details}
        loading={submitting}
        onPix={flow.onPix}
        onClose={onTryAgain}
        planAmount={planAmountNumber}
        pixGenerationFailed={pixGenerationFailed}
      />
      <ModalCheckoutPixPayment
        isOpen={result === "pix-success"}
        pixPaymentData={pixPaymentData}
        formikValues={formik.values}
        onAccessPlatform={onAccessPlatform}
      />
      <OabPartnershipModal
        isOpen={isOabPartnershipModalOpen}
        urlCoupon={formik.values.coupom}
        onApplyCoupon={handleOabPartnershipCouponApplied}
        onClose={() => setIsOabPartnershipModalOpen(false)}
      />
      <InfoprodutoresSuccessModal
        open={result === "infoprodutores-success"}
        coupon={infoprodutoresCoupon}
        email={formik.values.email}
        onAccess={handleAccessInfoprodutor}
      />
    </>
  );
}
